/**
 * Impersonation Helper Utility
 * 
 * Provides functions to detect impersonation mode and return the correct userId
 * for database operations.
 * 
 * When superadmin impersonates a user:
 * - sessionStorage contains "impersonation_session" with viewedUserId
 * - All database operations should use viewedUserId, not superadmin's userId
 * - This ensures edits save to the impersonated user's profile
 */

export interface ImpersonationSession {
  sessionToken: string
  superadminId: string
  superadminEmail: string
  viewedUserId: string
  viewedUserEmail: string
  viewedUserName: string
  viewedRole: string
  enteredAt: string
}

/**
 * Get the effective userId for database operations
 * 
 * @param fallbackUserId - The real user's ID from lumi.auth.user (fallback)
 * @returns The userId to use for database operations (impersonated or real)
 */
export function getEffectiveUserId(fallbackUserId: string): string {
  const session = getImpersonationSession()
  
  if (session) {
    // In impersonation mode - use impersonated user's ID
    return session.viewedUserId
  }
  
  // Not impersonating - use real user's ID
  return fallbackUserId
}

/**
 * Check if currently in impersonation mode
 * 
 * @returns true if superadmin is viewing as another user
 */
export function isImpersonating(): boolean {
  return getImpersonationSession() !== null
}

/**
 * Get the active impersonation session
 * 
 * @returns ImpersonationSession object or null
 */
export function getImpersonationSession(): ImpersonationSession | null {
  try {
    const sessionData = sessionStorage.getItem("impersonation_session")
    if (!sessionData) return null
    
    const session: ImpersonationSession = JSON.parse(sessionData)
    
    // Check if session expired (1 hour)
    const enteredAt = new Date(session.enteredAt)
    const now = new Date()
    const hoursSinceEntered = (now.getTime() - enteredAt.getTime()) / (1000 * 60 * 60)
    
    if (hoursSinceEntered > 1) {
      // Session expired - clear it
      sessionStorage.removeItem("impersonation_session")
      return null
    }
    
    return session
  } catch (error) {
    console.error("Failed to parse impersonation session:", error)
    return null
  }
}

/**
 * Get user info for audit logs
 * 
 * @param realUserId - The authenticated user's ID
 * @returns Object with userId and audit info
 */
export function getUserInfoForAudit(realUserId: string) {
  const session = getImpersonationSession()
  
  if (session) {
    return {
      effectiveUserId: session.viewedUserId,
      actorUserId: session.superadminId,
      isImpersonating: true,
      sessionToken: session.sessionToken
    }
  }
  
  return {
    effectiveUserId: realUserId,
    actorUserId: realUserId,
    isImpersonating: false,
    sessionToken: null
  }
}

/**
 * Get the viewed user's display name from impersonation session
 * 
 * @returns Viewed user's name or null if not impersonating
 */
export function getViewedUserName(): string | null {
  const session = getImpersonationSession()
  return session ? session.viewedUserName : null
}

/**
 * Create audit log entry for impersonation actions
 * 
 * @param action - Action performed (e.g., "profile_edit", "document_upload")
 * @param entityType - Type of entity (e.g., "UserProfiles", "Education", "Documents")
 * @param entityId - ID of the entity being modified
 * @param details - Additional details about the action
 */
export async function logImpersonationAction(
  action: string,
  entityType: string,
  entityId: string,
  details?: Record<string, any>
) {
  const session = getImpersonationSession()
  
  if (!session) {
    // Not impersonating, no need to log
    return
  }

  try {
    // Import lumi dynamically to avoid circular dependencies
    const { lumi } = await import('./lumi')
    
    await lumi.entities.AuditLogs.create({
      sessionToken: session.sessionToken,
      superadminId: session.superadminId,
      superadminEmail: session.superadminEmail,
      targetUserId: session.viewedUserId,
      targetUserEmail: session.viewedUserEmail,
      targetUserName: session.viewedUserName,
      targetRole: session.viewedRole,
      action,
      entityType,
      entityId,
      details: details || {},
      ipAddress: null, // Could be added via backend
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString()
    })
  } catch (error) {
    console.error('Failed to log impersonation action:', error)
    // Don't throw - audit logging should not break the main flow
  }
}

/**
 * End the current impersonation session
 * Updates the session status in database and clears sessionStorage
 */
export async function endImpersonationSession() {
  const session = getImpersonationSession()
  
  if (!session) {
    return
  }

  try {
    const { lumi } = await import('./lumi')
    
    // Find and update the session in database
    const sessions = await lumi.entities.ImpersonationSessions.list({
      filter: { session_token: session.sessionToken }
    })
    
    if (sessions.list.length > 0) {
      await lumi.entities.ImpersonationSessions.update(sessions.list[0]._id, {
        status: "exited",
        exited_at: new Date().toISOString()
      })
    }
    
    // Clear sessionStorage
    sessionStorage.removeItem("impersonation_session")
  } catch (error) {
    console.error('Failed to end impersonation session:', error)
  }
}
